import React, { ComponentProps } from 'react';
import ILink from '../../../types/ILink';
import { PagePackStates } from '../../../types/api/PagePackState';
import { IFileItem } from '../../../types/api/FileItem';
import { Pagination, PaginationItemWithPageLink } from '../../../_lib/components/Pagination';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import ResultListSettings from '../../../components/molecules/ResultListSettings/ResultListSettings';
import {
  Table,
  TableBody,
  TableCell,
  TableCellDescription,
  TableCellPageLink,
  TableHead,
  TableModifier,
  TableRow,
} from '../../../components/organisms/Table';
import { murmurhash } from '../../../lib/js/hash';
import { DateRanger } from '../../../components/molecules/DateRanger/DateRanger';
import { asView } from '../../../_lib/ViewFetcher';
import { ICategory } from '../../../types/ICategory';

type Event = {
  teaserText: string;
  pageLink: ILink;
  eventStartDate: string;
  eventEndDate: string;
  packState: PagePackStates;
  allDayEvent: boolean;
  multiDayEvent: boolean;
  attachments?: IFileItem[];
  categories?: ICategory[];
  integrated?: boolean;
};

type EventListProps = {
  searchResults: {
    events?: Event[];
    sort: ILink[];
    facets?: {
      eventStartDate: {
        url: string | null;
        name: string;
        resultNumber: number;
      }[];
    };
    pageItems?: PaginationItemWithPageLink[];
  };
};

function EventListDetailsDefault({ searchResults }: EventListProps) {
  const { t } = useSonTranslation('EventList');
  const titleLabel = t('titleLabel');
  const shortDescriptionLabel = t('shortDescriptionLabel');
  const eventDateLabel = t('eventDateLabel');

  return (
    <div className="event-list">
      <ResultListSettings sort={searchResults.sort} filter={searchResults.facets?.eventStartDate} />
      {searchResults.events && searchResults.events.length > 0 && (
        <>
          <Table caption={t('caption')}>
            <TableHead>
              <TableRow>
                <TableCell modifier={TableModifier.XLWIDTH}>{titleLabel}</TableCell>
                <TableCell>{shortDescriptionLabel}</TableCell>
                <TableCell modifier={TableModifier.MWIDTH}>{eventDateLabel}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {searchResults.events.map((resultItem) => {
                return (
                  <TableRow key={murmurhash(resultItem)}>
                    <TableCellPageLink
                      label={titleLabel}
                      pageLink={resultItem.pageLink}
                      pagePackState={resultItem.packState}
                      categories={resultItem.categories}
                      integrated={resultItem.integrated}
                    />
                    <TableCellDescription label={shortDescriptionLabel} description={resultItem.teaserText} />
                    <TableCell dataHead={eventDateLabel}>
                      <DateRanger
                        start={resultItem.eventStartDate}
                        end={resultItem.eventEndDate}
                        multiDayEvent={resultItem.multiDayEvent}
                        allDayEvent={resultItem.allDayEvent}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Pagination pageItems={searchResults.pageItems} />
        </>
      )}
    </div>
  );
}

export const EventListDetailsDefaultView = asView(EventListDetailsDefault);
export type EventListDetailsDefaultView = ComponentProps<typeof EventListDetailsDefaultView>;
